/**
 * Centralized API service layer.
 *
 * Every backend call goes through this file so that:
 *   - base URL is resolved once
 *   - error handling is consistent
 *   - it's easy to swap transport later (e.g. add auth headers)
 */

const BASE = process.env.REACT_APP_BACKEND_URL || '';

/* ── helpers ─────────────────────────────────────────────── */

async function request(url, options = {}) {
  const res = await fetch(`${BASE}${url}`, options);
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch (e) {
      // Response body is not JSON, use HTTP status
      console.debug('Non-JSON error response:', e.message);
    }
    throw new Error(detail);
  }
  return res;
}

async function jsonRequest(url, options = {}) {
  const res = await request(url, options);
  return res.json();
}

/* ── Health ──────────────────────────────────────────────── */

export async function healthCheck() {
  return jsonRequest('/api/health');
}

/* ── Upload / Import ─────────────────────────────────────── */

export async function uploadFile(file) {
  const form = new FormData();
  form.append('file', file);
  return jsonRequest('/api/upload', { method: 'POST', body: form });
}

export async function importGoogleSheet(url) {
  const form = new FormData();
  form.append('url', url);
  return jsonRequest('/api/import-google-sheets', { method: 'POST', body: form });
}

/* ── Chat / Q&A ──────────────────────────────────────────── */

export async function sendChatMessage({ datasetId, sheetName, question, provider, apiKey, model }) {
  const form = new FormData();
  form.append('dataset_id', datasetId);
  form.append('sheet_name', sheetName);
  form.append('question', question);
  form.append('provider', provider || 'openai');
  form.append('api_key', apiKey);
  form.append('model', model || '');
  return jsonRequest('/api/chat', { method: 'POST', body: form });
}

export async function sendChatMessageStream({ datasetId, sheetName, question, provider, apiKey, model, onToken, onDone, onError }) {
  const form = new FormData();
  form.append('dataset_id', datasetId);
  form.append('sheet_name', sheetName);
  form.append('question', question);
  form.append('provider', provider || 'openai');
  form.append('api_key', apiKey);
  form.append('model', model || '');

  try {
    const res = await fetch(`${BASE}/api/chat/stream`, { method: 'POST', body: form });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try { const body = await res.json(); detail = body.detail || detail; } catch (e) { /* ignore */ }
      throw new Error(detail);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.token) onToken(data.token);
          else if (data.done) onDone(data.model);
          else if (data.error) onError(new Error(data.error));
        } catch (e) { /* skip malformed */ }
      }
    }
  } catch (err) {
    onError(err);
  }
}

export async function fetchChatHistory(datasetId, sheetName) {
  const qs = sheetName ? `?sheet_name=${encodeURIComponent(sheetName)}` : '';
  return jsonRequest(`/api/chat-history/${datasetId}${qs}`);
}

/* ── Export ───────────────────────────────────────────────── */

export async function exportSummary(datasetId, sheetName, format = 'markdown') {
  const url = `/api/export/${datasetId}/${encodeURIComponent(sheetName)}?format=${format}`;
  const res = await request(url);
  return res.blob();
}

/* ── Analysis ─────────────────────────────────────────────── */

export async function analyzeSheet(datasetId, sheetName) {
  return jsonRequest(`/api/analyze/${datasetId}/sheet/${encodeURIComponent(sheetName)}`, {
    method: 'POST'
  });
}

/* ── Dataset history ─────────────────────────────────────── */

export async function listDatasets(limit = 20, offset = 0) {
  return jsonRequest(`/api/datasets?limit=${limit}&offset=${offset}`);
}

export async function getDataset(datasetId) {
  return jsonRequest(`/api/datasets/${datasetId}`);
}

/* ── Models catalogue ────────────────────────────────────── */

export async function fetchModels() {
  const data = await jsonRequest('/api/models');
  return data.models || [];
}
